import { assetStrategyDocument, readStrategyPosition, writeStrategyPosition, strategyPositionQuery } from '../graphql/assetStrategies';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { parseFloatFromString, parseReadableNumberToFloat } from '../utils/formatters';
import { useMutation, useLazyQuery } from '@apollo/client';
import { SAVE_EXIT_STRATEGY_LEVELS } from '../graphql/mutations/exitStrategy';
import { PREVIEW_EXIT_STRATEGY } from '../graphql/queries/exitStrategyPreview';
import { 
  DISTRIBUTION_CURVES, 
  calculateDistributedPercentages,
  MIN_TAKE_INITIALS_MULTIPLE
} from '../utils/distributionCurves';
import ExitStrategySettingsForm from './ExitStrategySettingsForm';
import ExitStrategyPreviewPanel from './ExitStrategyPreviewPanel';

const ExitStrategyPlanner = ({ position, token, averageCost, holdingsAmount, portfolioId, updateCache, onClose, templateValues, onFormModified, selectedTemplateId, onRemove, assetKind = 'crypto' }) => {
  // Target multiple state
  const [targetMultiple, setTargetMultiple] = useState('3'); // Default to 3x
  
  // Take initials state
  const [takeInitialsEnabled, setTakeInitialsEnabled] = useState(false);
  const [takeInitialsMultiple, setTakeInitialsMultiple] = useState('');
  
  // HODL amount state
  const [hodlEnabled, setHodlEnabled] = useState(false);
  const [hodlPercentage, setHodlPercentage] = useState(10); // Default 10%
  
  // Exit levels state
  const [exitLevelsCount, setExitLevelsCount] = useState(3); // Default to 3 levels
  const [exitLevelsSpread, setExitLevelsSpread] = useState(10); // Default to 10% spread
  
  // Advanced distribution state
  const [advancedDistributionEnabled, setAdvancedDistributionEnabled] = useState(false);
  const [distributionFocus, setDistributionFocus] = useState(50); // 0=earlier, 50=target, 100=later
  const [distributionIntensity, setDistributionIntensity] = useState(50); // 0=minimal, 100=maximum
  const [distributionCurve, setDistributionCurve] = useState(DISTRIBUTION_CURVES.BELL); // Default to bell curve
  
  // Planned transactions state
  const [plannedLevels, setPlannedLevels] = useState([]);
  
  // Backend preview state
  const [backendPreview, setBackendPreview] = useState(null);
  const [previewError, setPreviewError] = useState(false);
  const previewDebounceRef = useRef(null);
  const previewRequestRef = useRef(0);
  
  // Save state
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [savingLevels, setSavingLevels] = useState(false);

  
  // Get token details for market cap calculation
  const marketData = token.marketData || {};
  const currentMarketCap = parseReadableNumberToFloat(marketData.marketCap || '0');
  const currentPrice = parseFloatFromString(marketData.currentPrice || '0');
  // Base price source: 'avg' (average buy) or 'current'
  const [baseSource, setBaseSource] = useState(() => {
    const avg = parseFloatFromString(averageCost);
    return avg && avg > 0 ? 'avg' : 'current';
  });

  // Apply template values when template is selected or changes
  // Template is the source of truth when assigned; otherwise use component defaults
  useEffect(() => {
    if (templateValues) {
      if (templateValues.targetMultiple !== undefined) setTargetMultiple(String(templateValues.targetMultiple));
      if (templateValues.baseSource !== undefined) setBaseSource(templateValues.baseSource === 'avg' ? 'avg' : 'current');
      if (templateValues.exitLevelsCount !== undefined) setExitLevelsCount(Number(templateValues.exitLevelsCount));
      if (templateValues.exitLevelsSpread !== undefined) setExitLevelsSpread(Number(templateValues.exitLevelsSpread));
      if (templateValues.takeInitialsEnabled !== undefined) setTakeInitialsEnabled(!!templateValues.takeInitialsEnabled);
      if (templateValues.takeInitialsMultiple !== undefined) setTakeInitialsMultiple(String(templateValues.takeInitialsMultiple || ''));
      if (templateValues.hodlEnabled !== undefined) setHodlEnabled(!!templateValues.hodlEnabled);
      if (templateValues.hodlPercentage !== undefined) setHodlPercentage(Number(templateValues.hodlPercentage));
      if (templateValues.advancedDistributionEnabled !== undefined) setAdvancedDistributionEnabled(!!templateValues.advancedDistributionEnabled);
      if (templateValues.distributionCurve !== undefined) setDistributionCurve(templateValues.distributionCurve);
      if (templateValues.distributionFocus !== undefined) setDistributionFocus(Number(templateValues.distributionFocus));
      if (templateValues.distributionIntensity !== undefined) setDistributionIntensity(Number(templateValues.distributionIntensity));
    }
  }, [templateValues]);

  const selectedBasePrice = useMemo(() => {
    const avg = parseFloatFromString(averageCost);
    const cur = currentPrice && currentPrice > 0 ? currentPrice : 1;
    if (baseSource === 'avg') {
      return avg && avg > 0 ? avg : cur;
    }
    return cur;
  }, [baseSource, averageCost, currentPrice]);

  // Setup mutation for saving exit strategy levels
  const [saveExitStrategyLevels] = useMutation(assetStrategyDocument(SAVE_EXIT_STRATEGY_LEVELS, assetKind));
  
  // Setup lazy query for backend preview
  const [fetchBackendPreview] = useLazyQuery(assetStrategyDocument(PREVIEW_EXIT_STRATEGY, assetKind), {
    fetchPolicy: 'network-only',
    onError: (err) => {
      setPreviewError(true);
      console.error('Backend preview error:', err);
    }
  });

  // Debounced backend preview - fetch from server for consistent calculations
  useEffect(() => {
    const requestId = ++previewRequestRef.current;
    setBackendPreview(null);
    setPreviewError(false);
    // Clear any pending debounce
    if (previewDebounceRef.current) {
      clearTimeout(previewDebounceRef.current);
    }
    
    // Don't fetch if we don't have required data
    if (!portfolioId || !token?.id) {
      return;
    }
    
    // Debounce the preview call to avoid too many requests
    previewDebounceRef.current = setTimeout(() => {
      const variables = {
        portfolioId,
        tokenId: token.id,
      };
      
      // Use template ID if one is selected, otherwise use custom params
      if (selectedTemplateId) {
        variables.templateId = selectedTemplateId;
      } else {
        // Build params for custom strategy preview
        variables.params = {
          targetMultiple: String(targetMultiple),
          baseSource: baseSource === 'avg' ? 'AVG' : 'CURRENT',
          exitLevelsCount: exitLevelsCount,
          exitLevelsSpread: exitLevelsSpread,
          takeInitialsEnabled: takeInitialsEnabled,
          takeInitialsMultiple: takeInitialsEnabled ? String(takeInitialsMultiple || '') : null,
          hodlEnabled: hodlEnabled,
          hodlPercentage: hodlEnabled ? hodlPercentage : null,
          advancedDistributionEnabled: advancedDistributionEnabled,
          distributionCurve: advancedDistributionEnabled ? distributionCurve : null,
          distributionFocus: advancedDistributionEnabled ? distributionFocus : null,
          distributionIntensity: advancedDistributionEnabled ? distributionIntensity : null,
        };
      }
      
      fetchBackendPreview({ variables }).then(({ data, error }) => {
        if (requestId === previewRequestRef.current && !error) {
          setBackendPreview(data?.previewExitStrategy || null);
        }
      }).catch(() => { /* The query's onError reports the failure. */ });
    }, 300); // 300ms debounce
    
    // Cleanup on unmount
    return () => {
      previewRequestRef.current += 1;
      if (previewDebounceRef.current) {
        clearTimeout(previewDebounceRef.current);
      }
    };
  }, [
    portfolioId, token?.id, selectedTemplateId, assetKind, holdingsAmount, averageCost, currentPrice,
    targetMultiple, baseSource, exitLevelsCount, exitLevelsSpread,
    takeInitialsEnabled, takeInitialsMultiple, hodlEnabled, hodlPercentage,
    advancedDistributionEnabled, distributionCurve, distributionFocus, distributionIntensity,
    fetchBackendPreview
  ]);

  // Holdings helpers
  const numericHoldings = parseFloatFromString(holdingsAmount);
  const hasHoldingsInput = holdingsAmount !== undefined && holdingsAmount !== null && holdingsAmount !== '';
  const previewHoldings = hasHoldingsInput ? numericHoldings : 1000;
  const isZeroHoldings = hasHoldingsInput && numericHoldings <= 0;

  
  // Get the current distribution percentages based on settings
  const getDistributionPercentages = useCallback((count, totalPercentage) => {
    // Only use advanced distribution if enabled
    if (!advancedDistributionEnabled) {
      return Array(count).fill(totalPercentage / count);
    }
    
    // Use the utility function to calculate percentages
    return calculateDistributedPercentages(
      count,
      totalPercentage,
      distributionCurve,
      distributionFocus,
      distributionIntensity
    );
  }, [
    advancedDistributionEnabled,
    distributionCurve,
    distributionFocus,
    distributionIntensity
  ]);
  
  // Generate updated planned levels when dependencies change
  useEffect(() => {
    if (!targetMultiple) {
      return;
    }
    
    // Determine base price based on selection
    const effectiveBasePrice = selectedBasePrice;
    
    const targetMult = parseFloatFromString(targetMultiple);
    const targetPrice = effectiveBasePrice * targetMult;
    
    // Create new planned levels
    let newLevels = [];
    
    // Track remaining percentage to distribute after take initials
    // Note: HODL is handled separately - these percentages represent % of available (non-HODL) holdings
    let remainingPercentage = 100;
    
    // Add take initials level if enabled
    if (takeInitialsEnabled && takeInitialsMultiple) {
      const tiMult = parseFloatFromString(takeInitialsMultiple);
      
      // Calculate percentage needed to get 100% of costs back
      // If multiple is X, we need to sell 100/X percent to get full investment back
      const tiPercentage = Math.min(remainingPercentage, 100 / tiMult);
      
      newLevels.push({
        percentage: Math.round(tiPercentage * 1000000) / 1000000, // Round to 6 decimal places to avoid floating point issues
        price: effectiveBasePrice * tiMult
      });
      
      // Update remaining percentage for other levels
      remainingPercentage -= tiPercentage;
    }
    
    // Calculate exit levels around target price
    if (exitLevelsCount === 1) {
      // If only one level, sell remaining percentage at target price
      newLevels.push({
        percentage: Math.round(remainingPercentage * 1000000) / 1000000, // Round to 6 decimal places to avoid floating point issues
        price: targetPrice
      });
    } else {
      // For multiple levels, distribute around target price
      // Calculate percentages based on distribution settings
      const percentages = getDistributionPercentages(exitLevelsCount, remainingPercentage);
      
      // Calculate the range to spread levels (using custom spread value)
      const spread = exitLevelsSpread / 100; // Convert percentage to decimal
      const totalSpread = spread * (exitLevelsCount - 1);
      const startPercentage = 1 - (totalSpread / 2);
      
      for (let i = 0; i < exitLevelsCount; i++) {
        // Calculate price percentage relative to target (from -spread% to +spread% per level)
        const pricePercentage = startPercentage + (spread * i);
        
        // NOTE: We no longer floor prices - users may intentionally plan exits below cost
        // for risk management, tax loss harvesting, or quick exits from risky positions.
        const levelPrice = targetPrice * pricePercentage;
        
        newLevels.push({
          percentage: Math.round(percentages[i] * 1000000) / 1000000, // Round to 6 decimal places to avoid floating point issues
          price: levelPrice
        });
      }
    }
    
    setPlannedLevels(newLevels);
  }, [
    targetMultiple, 
    takeInitialsEnabled, 
    takeInitialsMultiple, 
    averageCost, 
    currentPrice, 
    selectedBasePrice,
    hodlEnabled, 
    hodlPercentage, 
    exitLevelsCount, 
    exitLevelsSpread, 
    getDistributionPercentages,
    holdingsAmount
  ]);
  
  // Calculated values
  const targetPrice = useMemo(() => {
    const effectiveBasePrice = selectedBasePrice;
    const targetMult = parseFloatFromString(targetMultiple) || 0;
    return effectiveBasePrice * targetMult;
  }, [selectedBasePrice, targetMultiple]);
  
  const takeInitialsPrice = useMemo(() => {
    if (!takeInitialsEnabled || !takeInitialsMultiple) return 0;
    const effectiveBasePrice = selectedBasePrice;
    const takeInitialsMult = parseFloatFromString(takeInitialsMultiple) || 0;
    return effectiveBasePrice * takeInitialsMult;
  }, [selectedBasePrice, takeInitialsEnabled, takeInitialsMultiple]);
  
  // Memoize the calculation function
  const memoizedCalculateTotalProjectedProfit = useCallback(() => {
    const avgCost = parseFloatFromString(averageCost);
    const totalHoldings = parseFloatFromString(holdingsAmount);
    
    // Use fallback values for preview if actual values are not available
    // Prefer current price over arbitrary fallback for more meaningful calculations
    const effectiveAvgCost = avgCost && avgCost > 0 ? avgCost : (currentPrice && currentPrice > 0 ? currentPrice : 1);
    const effectiveTotalHoldings = hasHoldingsInput ? totalHoldings : 1000;
    const availableHoldings = hodlEnabled 
      ? (effectiveTotalHoldings * (100 - hodlPercentage) / 100)
      : effectiveTotalHoldings;
    
    let totalProfit = 0;
    let totalSold = 0;
    let totalCost = 0;
    
    plannedLevels.forEach(level => {
      // The level.percentage is already calculated relative to available holdings
      // So we apply it to total holdings to get the correct absolute amount
      const amountToSell = (level.percentage / 100) * availableHoldings;
      totalSold += amountToSell;
      
      const levelCost = amountToSell * effectiveAvgCost;
      totalCost += levelCost;
      
      const levelProfit = amountToSell * (level.price - effectiveAvgCost);
      totalProfit += levelProfit;
    });
    
    // When HODL is enabled, we need to add the HODL percentage to ensure soldPercentageOfTotal 
    // is correctly displayed as a percentage of what we're actually selling
    // If HODL is 20%, and we're selling 100% of available holdings, that's only 80% of total
    const soldPercentageOfTotal = effectiveTotalHoldings > 0 ? (totalSold / effectiveTotalHoldings) * 100 : 0;
    const effectivePercentageSold = soldPercentageOfTotal;
    
    // Different ways to calculate the return
    // 1. Return on the sold portion only (as a multiple of initial investment)
    // ROI of 100% = 2x, 200% = 3x, etc.
    const roiOnSoldPortion = totalCost > 0 ? (totalProfit / totalCost) : 0;
    const returnMultipleOnSoldPortion = 1 + roiOnSoldPortion;
    
    // 2. Return on the total investment (including HODL amount)
    const totalInvestment = effectiveTotalHoldings * effectiveAvgCost;
    const roiOnTotalInvestment = totalInvestment > 0 ? (totalProfit / totalInvestment) : 0;
    const returnMultipleOnTotalInvestment = 1 + roiOnTotalInvestment;
    
    // 3. Estimated ROI if selling everything at the average exit price
    let estimatedFullReturn = 0;
    if (totalSold > 0) {
      // Calculate average exit price
      const avgExitPrice = totalProfit / totalSold + effectiveAvgCost;
      // Calculate profit if all holdings were sold at this price
      const estimatedTotalProfit = effectiveTotalHoldings * (avgExitPrice - effectiveAvgCost);
      estimatedFullReturn = totalInvestment > 0 ? (estimatedTotalProfit / totalInvestment) : 0;
    }
    
    // Calculate what percentage of our investment we're actually selling
    const percentOfInvestmentSold = totalCost > 0 ? (totalCost / totalInvestment * 100) : 0;
    
    return {
      profit: totalProfit,
      soldPercentage: soldPercentageOfTotal,
      effectivePercentageSold: effectivePercentageSold,  
      soldAmount: totalSold,
      roiOnSoldPortion: roiOnSoldPortion,
      returnMultipleOnSoldPortion: returnMultipleOnSoldPortion,
      roiOnTotalInvestment: roiOnTotalInvestment,
      returnMultipleOnTotalInvestment: returnMultipleOnTotalInvestment,
      estimatedFullReturn: estimatedFullReturn,
      percentOfInvestmentSold: percentOfInvestmentSold
    };
  }, [plannedLevels, holdingsAmount, averageCost, hodlEnabled, hodlPercentage, currentPrice, hasHoldingsInput]);
  
  const projectedResults = useMemo(() => {
    return memoizedCalculateTotalProjectedProfit();
  }, [memoizedCalculateTotalProjectedProfit]);
  
  // Define min, max values for the sliders
  const minTakeInitialsMultiple = MIN_TAKE_INITIALS_MULTIPLE;
  const maxTakeInitialsMultiple = targetMultiple ? parseFloatFromString(targetMultiple) - 0.1 : 3;

  // Form value and context for shared ExitStrategySettingsForm
  const formValue = useMemo(() => ({
    targetMultiple,
    baseSource,
    exitLevelsCount,
    exitLevelsSpread,
    takeInitialsEnabled,
    takeInitialsMultiple: takeInitialsMultiple || '',
    hodlEnabled,
    hodlPercentage,
    advancedDistributionEnabled,
    distributionCurve,
    distributionFocus,
    distributionIntensity,
  }), [
    targetMultiple, baseSource, exitLevelsCount, exitLevelsSpread,
    takeInitialsEnabled, takeInitialsMultiple, hodlEnabled, hodlPercentage,
    advancedDistributionEnabled, distributionCurve, distributionFocus, distributionIntensity,
  ]);

  const handleFormChange = useCallback((field, val) => {
    switch (field) {
      case 'targetMultiple': setTargetMultiple(val); break;
      case 'baseSource': setBaseSource(val); break;
      case 'exitLevelsCount': setExitLevelsCount(val); break;
      case 'exitLevelsSpread': setExitLevelsSpread(val); break;
      case 'takeInitialsEnabled': setTakeInitialsEnabled(val); break;
      case 'takeInitialsMultiple': setTakeInitialsMultiple(val); break;
      case 'hodlEnabled': setHodlEnabled(val); break;
      case 'hodlPercentage': setHodlPercentage(val); break;
      case 'advancedDistributionEnabled': setAdvancedDistributionEnabled(val); break;
      case 'distributionCurve': setDistributionCurve(val); break;
      case 'distributionFocus': setDistributionFocus(val); break;
      case 'distributionIntensity': setDistributionIntensity(val); break;
      default: break;
    }
    if (onFormModified) onFormModified();
  }, [onFormModified]);

  const formContext = useMemo(() => ({
    selectedBasePrice,
    targetPrice,
    showBaseSourceToggle: parseFloatFromString(averageCost) > 0,
    takeInitialsPrice,
    minTakeInitialsMultiple,
    maxTakeInitialsMultiple,
    hodlDisplayText: hodlEnabled ? `${((parseFloatFromString(holdingsAmount) || 1000) * (hodlPercentage / 100)).toFixed(6)} ${token?.ticker || 'TOKEN'}` : undefined,
    tokenTicker: token?.ticker || 'TOKEN',
  }), [
    selectedBasePrice, targetPrice, averageCost, takeInitialsPrice,
    minTakeInitialsMultiple, maxTakeInitialsMultiple,
    hodlEnabled, hodlPercentage, holdingsAmount, token?.ticker,
  ]);

  const previewContext = useMemo(() => ({
    selectedBasePrice,
    targetMultiple,
    currentPrice,
    currentMarketCap,
    previewHoldings,
    hodlEnabled,
    hodlPercentage,
    takeInitialsEnabled,
  }), [
    selectedBasePrice, targetMultiple, currentPrice, currentMarketCap,
    previewHoldings, hodlEnabled, hodlPercentage, takeInitialsEnabled,
  ]);
  
  // Handler for saving exit strategy levels
  const handleSaveLevels = async (e) => {
    // Prevent any default form submission behavior
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!plannedLevels.length) {
      return;
    }
    
    if (!position) {
      return;
    }
    
    if (!token) {
      return;
    }
    
    if (!token.id) {
      return;
    }
    
    // Prevent saving when there are explicitly zero holdings
    if (isZeroHoldings) {
      return;
    }

    setSavingLevels(true);
    setSaveSuccess(false);
    setSaveError(false);
    
    try {
      // Get total holdings for amount calculation
      const totalHoldings = parseFloatFromString(holdingsAmount);
      
      // Calculate available holdings (total minus HODL amount if enabled)
      const availableHoldings = hodlEnabled 
        ? totalHoldings * (100 - hodlPercentage) / 100
        : totalHoldings;
      
      let levelAmounts;
      
      if (hodlEnabled) {
        // When HODL is enabled, apply percentages to available holdings (not total)
        levelAmounts = plannedLevels.map(level => {
          return (parseFloatFromString(level.percentage) / 100) * availableHoldings;
        });
      } else {
        // When no HODL, calculate amounts by subtracting from remaining holdings
        let remainingHoldings = totalHoldings;
        levelAmounts = [];
        
        for (let i = 0; i < plannedLevels.length; i++) {
          const level = plannedLevels[i];
          const percentage = parseFloatFromString(level.percentage);
          
          if (i === plannedLevels.length - 1) {
            // Last level gets all remaining holdings
            levelAmounts.push(remainingHoldings);
          } else {
            // Calculate amount for this level
            const amount = (percentage / 100) * totalHoldings;
            levelAmounts.push(amount);
            remainingHoldings -= amount;
          }
        }
      }
      
      // Ensure no amount is negative (shouldn't happen, but safety check)
      levelAmounts = levelAmounts.map(amount => Math.max(0, amount));
      
      // Format levels for the mutation - convert to actual token amounts
      let levels = plannedLevels.map((level, index) => {
        const numPrice = parseFloatFromString(level.price);
        
        // Return amount and price as formatted strings
        return {
          amount: levelAmounts[index].toFixed(8),
          price: numPrice.toFixed(8)
        };
      });
      
      // Build params object to save form state
      const params = {
        targetMultiple: String(targetMultiple),
        baseSource: baseSource === 'avg' ? 'AVG' : 'CURRENT',
        exitLevelsCount: exitLevelsCount,
        exitLevelsSpread: exitLevelsSpread,
        takeInitialsEnabled: takeInitialsEnabled,
        takeInitialsMultiple: takeInitialsEnabled && takeInitialsMultiple ? String(takeInitialsMultiple) : null,
        hodlEnabled: hodlEnabled,
        hodlPercentage: hodlEnabled ? hodlPercentage : null,
        advancedDistributionEnabled: advancedDistributionEnabled,
        distributionCurve: advancedDistributionEnabled && distributionCurve ? distributionCurve : null,
        distributionFocus: advancedDistributionEnabled ? distributionFocus : null,
        distributionIntensity: advancedDistributionEnabled ? distributionIntensity : null,
      };
      
      // Call the mutation
      // Stock previews own exact amounts and portfolio-currency prices. Fetch
      // the current form again: never save a stale debounce or local rounding.
      if (assetKind === 'stock') {
        const { data, error } = await fetchBackendPreview({
          variables: {
            portfolioId,
            tokenId: token.id,
            ...(selectedTemplateId ? { templateId: selectedTemplateId } : { params }),
          },
        });
        const preview = data?.previewExitStrategy;
        if (error || !preview?.levels?.length) {
          throw new Error('Unable to preview stock exit levels');
        }
        levels = preview.levels.map(({ amount, price }) => ({ amount: String(amount), price: String(price) }));
        setBackendPreview(preview);
      }

      const result = await saveExitStrategyLevels({
        variables: {
          tokenId: token.id,
          levels: levels,
          portfolioId: portfolioId,
          templateId: selectedTemplateId || null,
          params: params
        },
        refetchQueries: [strategyPositionQuery(assetKind, token, portfolioId)],
        awaitRefetchQueries: true,
        update: (cache, { data }) => {
          try {
            const updatedExit = data?.saveExitStrategyLevels?.exitStrategy;
            if (data?.saveExitStrategyLevels?.success && updatedExit) {
              const existing = readStrategyPosition(cache, assetKind, token, portfolioId);
              if (existing?.tokenWithPosition?.position) {
                // Compute planned aggregates from updated exit strategy
                const plannedCostNum = parseFloatFromString(updatedExit.cost) || 0;
                const plannedProceedsNum = parseFloatFromString(updatedExit.proceeds) || 0;
                const plannedPnlNum = plannedProceedsNum - plannedCostNum;
                const plannedRoiPct = plannedCostNum > 0 ? (plannedPnlNum / plannedCostNum) * 100 : 0;
                const plannedMultipleNum = plannedCostNum > 0 ? (plannedProceedsNum / plannedCostNum) : 1.0;

                const updated = {
                  ...existing,
                  tokenWithPosition: {
                    ...existing.tokenWithPosition,
                    position: {
                      ...existing.tokenWithPosition.position,
                      exitStrategy: updatedExit,
                      planned: {
                        ...(existing.tokenWithPosition.position.planned || {}),
                        basisCost: plannedCostNum.toString(),
                        proceeds: plannedProceedsNum.toString(),
                        pnl: plannedPnlNum.toString(),
                        roi: plannedRoiPct.toString(),
                        multiple: plannedMultipleNum.toFixed(1)
                      },
                      plannedConservative: existing.tokenWithPosition.position.plannedConservative || null,
                    }
                  },
                };
                writeStrategyPosition(cache, assetKind, token, portfolioId, updated);
              }
            }
          } catch (_) {
            // ignore cache miss
          }
        }
      });
      
      if (result.data?.saveExitStrategyLevels?.success) {
        setSaveSuccess(true);
        try { toast.success('Exit strategy saved successfully!'); } catch (_) {}
        if (typeof onClose === 'function') {
          try { onClose(); } catch (_) {}
        }
        // Skip refetch; cache update above keeps UI consistent without flicker
      } else {
        setSaveError(true);
        console.error('Save failed:', result.data?.saveExitStrategyLevels?.error);
      }
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);

      // Note: cache is updated for exitStrategy; refetch ensures planned/aggregates update
    } catch (error) {
      console.error('Error saving exit strategy levels:', error);
      setSaveError(true);
      
      // Hide error message after 5 seconds
      setTimeout(() => {
        setSaveError(false);
      }, 5000);
    } finally {
      setSavingLevels(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <ExitStrategySettingsForm
        value={formValue}
        onChange={handleFormChange}
        context={formContext}
      />
      <ExitStrategyPreviewPanel
        title="Planned Transactions Preview"
        levels={assetKind === 'stock' ? backendPreview?.levels || [] : plannedLevels}
        backendPreview={backendPreview}
        useBackendLevels={assetKind === 'stock'}
        emptyMessage={assetKind === 'stock'
          ? previewError ? 'Unable to load stock strategy preview. Adjust the settings or try saving again to retry.' : 'Loading stock strategy preview…'
          : undefined}
        variant="full"
        context={previewContext}
        projectedResults={projectedResults}
        tokenTicker={token?.ticker || 'TOKEN'}
      >
        <div>
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="px-4 py-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
            >
              Remove strategy
            </button>
          )}
        </div>
        <div className="flex items-center">
          {saveSuccess && (
            <div className="mr-4 text-green-700 dark:text-green-400">
              Exit strategy saved successfully!
            </div>
          )}
          {saveError && (
            <div className="mr-4 text-red-700 dark:text-red-400">
              Error saving exit strategy levels. Please try again later.
            </div>
          )}
          <button
            type="button"
            onClick={handleSaveLevels}
            disabled={savingLevels || !plannedLevels.length || isZeroHoldings}
            className={`px-4 py-2 rounded-md ${
              savingLevels
                ? 'bg-gray-400 cursor-not-allowed pointer-events-none'
                : (!isZeroHoldings ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed pointer-events-none')
            } text-white font-medium transition-colors whitespace-nowrap`}
          >
            {savingLevels ? 'Saving...' : position.exitStrategy ? 'Update Exit Levels' : 'Save Exit Levels'}
          </button>
        </div>
      </ExitStrategyPreviewPanel>
    </div>
  );
};

export default ExitStrategyPlanner;